/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Button } from 'sharedUi/Button';
import { promptRunService } from '../../services/prompt-run.service';
import { extractErrorMessage } from '../../utils/error-handler';
import type { PromptRunResponse } from '../../types';

interface AdhocRunPanelProps {
  /** The model to test — sent as modelId. Persists nothing. */
  modelId: string;
}

const fmtCost = (cost?: number, currency?: string) => {
  if (cost === null || cost === undefined) return '-';
  const cur = currency ? `${currency} ` : '$';
  return `${cur}${cost.toFixed(cost < 1 ? 4 : 2)}`;
};

/** Ad-hoc "test this model" form + result. Meant to render inside a Dialog. */
const AdhocRunPanel = ({ modelId }: AdhocRunPanelProps) => {
  const [prompt, setPrompt] = useState('');
  const [system, setSystem] = useState('');
  const [maxTokens, setMaxTokens] = useState<number | null>(null);

  const mutation = useMutation<PromptRunResponse, any, void>({
    mutationFn: () =>
      promptRunService.executeAdhoc({
        prompt: prompt.trim(),
        system: system.trim() || undefined,
        modelId,
        maxTokens: maxTokens ?? undefined,
      }),
  });

  const result = mutation.data;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Prompt <span className="text-red-500">*</span>
          </label>
          <InputTextarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            placeholder="Enter a prompt to send to this model"
            className="w-full"
            autoResize
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">System</label>
          <InputTextarea
            value={system}
            onChange={(e) => setSystem(e.target.value)}
            rows={2}
            placeholder="Optional system prompt"
            className="w-full"
            autoResize
          />
        </div>
        <div className="flex flex-col gap-1 sm:max-w-xs">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Max Tokens</label>
          <InputNumber
            value={maxTokens}
            onValueChange={(e) => setMaxTokens(e.value ?? null)}
            useGrouping={false}
            placeholder="Model default"
            className="w-full"
          />
        </div>
        <div className="flex justify-end">
          <Button
            label={mutation.isPending ? 'Running…' : 'Run'}
            icon={mutation.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-play'}
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !prompt.trim()}
            size="small"
            data-testid="AdhocRun.Button.Run"
          />
        </div>
      </div>

      {mutation.isError && (
        <Message
          severity="error"
          text={extractErrorMessage(mutation.error, 'Failed to run model')}
          className="w-full"
        />
      )}

      {mutation.isPending && (
        <div className="flex items-center gap-2 p-3 text-sm text-ink-muted">
          <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="6" />
          Executing…
        </div>
      )}

      {result && (
        <div className="ds-card p-3">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <h3 className="text-sm font-semibold text-ink">Output</h3>
            {result.servedModel && (
              <Tag value={result.servedModel} severity="info" className="text-xs px-1.5 py-0.5" />
            )}
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
            <pre className="text-sm text-ink whitespace-pre-wrap font-sans break-words">
              {result.output || '-'}
            </pre>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 mt-3">
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Input Tokens</label>
              <p className="text-sm text-ink">{result.usage?.inputTokens ?? '-'}</p>
            </div>
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Output Tokens</label>
              <p className="text-sm text-ink">{result.usage?.outputTokens ?? '-'}</p>
            </div>
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Total Tokens</label>
              <p className="text-sm text-ink">{result.usage?.totalTokens ?? '-'}</p>
            </div>
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Cost</label>
              <p className="text-sm text-ink">{fmtCost(result.cost, result.currency)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdhocRunPanel;
